<template>
    <TransitionRoot appear :show="show" as="template">
        <Dialog as="div" @close="close" class="relative z-modal">
            <TransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black/25" />
            </TransitionChild>

            <div class="inset-0 fixed">
                <div
                    class="flex min-h-full items-center justify-center p-4 text-center"
                >
                    <TransitionChild
                        as="template"
                        enter="duration-300 ease-out"
                        enter-from="opacity-0 scale-95"
                        enter-to="opacity-100 scale-100"
                        leave="duration-200 ease-in"
                        leave-from="opacity-100 scale-100"
                        leave-to="opacity-0 scale-95"
                    >
                        <DialogPanel
                            :class="[teleportSource, width]"
                            ref="modal"
                        >
                            <div
                                :class="[
                                    headless
                                        ? 'relative'
                                        : [
                                              backgroundColor,
                                              'shadow-xl ring-1 overflow-hidden',
                                          ],
                                    'transform  ring-black/5 p-2 text-left align-middle transition-all rounded-modal',
                                ]"
                            >
                                <div
                                    ref="modalHeader"
                                    class="max-w-full flex pb-2"
                                    :class="[
                                        {
                                            'items-start': wrapTitle,
                                        },
                                        {
                                            'items-center': !wrapTitle,
                                        },
                                    ]"
                                >
                                    <div
                                        v-if="!headless"
                                        class="flex-grow pl-2 md:pl-4 py-3 text-lg font-semibold font-header mx-auto tracking-wide text-center lg:text-left"
                                    >
                                        <DialogTitle
                                            class="truncate"
                                            :class="[
                                                {
                                                    'text-wrap': wrapTitle,
                                                },
                                            ]"
                                        >
                                            <slot name="title">
                                                <h3 v-text="title" />
                                            </slot>
                                        </DialogTitle>

                                        <slot name="actions" />

                                        <!-- <DialogDescription
                                            v-if="description"
                                            as="p"
                                            class="text-sm font-normal max-w-full flex flex-wrap"
                                            v-html="description"
                                        /> -->
                                        <DialogDescription v-if="description">
                                            <p
                                                class="text-sm font-normal break-words font-sans tracking-wide leading-5 mt-1"
                                                v-html="description"
                                            />
                                        </DialogDescription>
                                    </div>
                                    <PrimaryButton
                                        v-if="closeButton"
                                        @click="close"
                                        :class="[
                                            {
                                                'absolute right-2 md:-right-8 -top-10 md:-top-8':
                                                    headless,
                                                'mt-2.5':
                                                    !headless && wrapTitle,
                                            },
                                            'size-10 flex-shrink-0 md:mr-2 group hover:shadow-inner hover:bg-accent-light/30 inline-flex justify-center rounded-full',
                                        ]"
                                    >
                                        <PhX
                                            class="size-7 flex-shrink-0 group-hover:scale-90 transition-transform duration-button-hover fill-current"
                                        />
                                    </PrimaryButton>
                                </div>
                                <div
                                    :class="[
                                        {
                                            'border-t border-border': !headless,
                                        },
                                        // make the inner content scrollable
                                        // screen height - header height - modal padding - some extra padding
                                        {
                                            'max-h-[calc(100vh-var(--modal-header-height)-theme(spacing.8)-theme(spacing.4))] scrollbar-gray':
                                                scrollable,
                                        },
                                        padding,
                                    ]"
                                >
                                    <!--
                                    We might have to delay showing the content until the modal has been rendered, only used when we have a teleportTarget.
                                    If we have selectpickers in the modal we need to be able to teleport them to the modal and that only works if the target is rendered before the selectpicker
                                -->
                                    <slot v-if="contentShown"></slot>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogPanel,
    DialogTitle,
    DialogDescription,
} from '@headlessui/vue'
import PrimaryButton from '@/Components/Elements/PrimaryButton.vue'
import { PhX } from '@phosphor-icons/vue'

export default {
    name: 'ModalTemplate',
    components: {
        TransitionRoot,
        TransitionChild,
        Dialog,
        DialogPanel,
        DialogTitle,
        DialogDescription,
        PrimaryButton,
        PhX,
    },

    props: {
        show: {
            type: Boolean,
            required: true,
        },
        title: {
            type: String,
        },
        wrapTitle: {
            type: Boolean,
            default: true,
        },
        description: {
            type: String,
            required: false,
        },
        headless: {
            type: Boolean,
            default: false,
        },
        scrollable: {
            type: Boolean,
            default: false,
        },
        width: {
            type: String,
            default: 'w-full',
        },
        backgroundColor: {
            type: String,
            default: 'bg-white',
        },
        closeButton: {
            type: Boolean,
            default: true,
        },
        teleportSource: {
            type: String,
            default: null,
        },
        padding: {
            type: String,
            default: 'p-4',
        },
    },

    emits: ['close'],

    data() {
        return {
            contentShown: false,
        }
    },

    created() {
        if (this.show) {
            // The nextTick is needed to ensure any teleport target has been rendered before we show the content
            setTimeout(() => {
                this.$nextTick(() => {
                    this.contentShown = true
                })
            }, 50)
        }
    },

    beforeUnmount() {
        this.disconnectResizeObserver()
    },

    watch: {
        show() {
            if (this.show) {
                this.$nextTick(() => {
                    this.setUpResizeObserver()
                    this.updateModalHeaderHeight()
                })

                if (this.teleportSource) {
                    setTimeout(() => {
                        this.$nextTick(() => {
                            this.contentShown = true
                        })
                    }, 50)
                } else {
                    this.contentShown = true
                }
            } else {
                this.disconnectResizeObserver()

                if (this.teleportSource) {
                    setTimeout(() => {
                        this.contentShown = false
                    }, 500)
                }
            }
        },
    },

    methods: {
        close() {
            this.$emit('close', false)
        },

        updateModalHeaderHeight() {
            if (this.$refs.modalHeader) {
                document.documentElement.style.setProperty(
                    '--modal-header-height',
                    `${this.$refs.modalHeader.offsetHeight}px`
                )
            }
        },

        setUpResizeObserver() {
            this.resizeObserver = new ResizeObserver(
                this.updateModalHeaderHeight
            )
            this.resizeObserver.observe(this.$refs.modalHeader)
        },

        disconnectResizeObserver() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect()
            }
        },
    },
}
</script>

<style scoped>
:root {
    --modal-header-height: 0px;
}
</style>
